/**
 * 群组管理页面 - 参考 openlink.chat 设计风格
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGroupById, getGroupRobots, refreshJoinToken } from '../../api/groups';
import { resetCount, approveRobot, rejectRobot, kickRobot } from '../../api/robots';
import { toast } from 'sonner';
import EditRobotModal from '../../components/EditRobotModal';

interface Robot {
  id: string;
  robot_ak: string | null;
  is_owner: boolean;
  robot_name: string;
  robot_identifier?: string;
  web_hook?: string;
  session_limit: number;
  status: string;
  joined_at: string;
  last_active?: string;
  total_used?: number;
  remaining?: number;
}

// 内联 SVG 图标
const Icons = {
  ArrowLeft: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  Copy: ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
  Bot: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  RefreshCw: ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Edit: ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  CheckCircle: ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  XCircle: ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  LogOut: ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
};

export default function GroupManage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<any>(null);
  const [robots, setRobots] = useState<Robot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRobot, setEditingRobot] = useState<any>(null);
  const [joinToken, setJoinToken] = useState<string>("");

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (group?.is_owner) loadJoinToken();
  }, [id, group?.is_owner]);

  async function loadData() {
    if (!id) return;
    try {
      const [groupData, robotsData] = await Promise.all([
        getGroupById(id),
        getGroupRobots(id, 1, 50),
      ]);
      setGroup(groupData);
      setRobots(robotsData.data?.items || robotsData.items || []);
    } catch (error) {
      toast.error('加载数据失败');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }

  async function loadJoinToken() {
    if (!id || group?.is_owner === false) return;
    try {
      const result: any = await refreshJoinToken(id);
      if (!result || !result.token) {
        throw new Error('响应结构异常');
      }
      setJoinToken(result.token);
    } catch (error: any) {
      console.error('loadJoinToken error:', error);
      toast.error("获取加群链接失败: " + (error?.message || '未知错误'));
    }
  }

  function copyToClipboard(text: string, message: string) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => toast.success(message));
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success(message);
      } catch {
        toast.error('复制失败，请手动复制');
      }
      document.body.removeChild(textarea);
    }
  }

  async function handleResetCount(robotId: string, robotName: string) {
    if (!confirm(`确定要重置"${robotName}"的会话计数吗？`)) return;
    try {
      await resetCount(robotId);
      toast.success('会话计数已重置');
      loadData();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  }

  function handleEditRobot(robot: any) {
    setEditingRobot(robot);
    setShowEditModal(true);
  }

  async function handleApprove(robotId: string, robotName: string) {
    try {
      await approveRobot(robotId);
      toast.success(`已批准 ${robotName} 加群`);
      loadData();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  }

  async function handleReject(robotId: string, robotName: string) {
    if (!confirm(`确定要拒绝 ${robotName} 的加群申请吗？`)) return;
    try {
      await rejectRobot(robotId);
      toast.success(`已拒绝 ${robotName} 的申请`);
      loadData();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  }

  async function handleKickRobot(robotId: string, robotName: string) {
    if (!confirm(`确定要踢出机器人 "${robotName}" 吗？踢出后该机器人将无法继续发送消息。`)) return;
    try {
      await kickRobot(robotId);
      toast.success(`已踢出 ${robotName}`);
      loadData();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  }

  function getOnlineStatus(robot: any): { label: string; dot: string } {
    if (robot.status === 'kicked') return { label: '', dot: '' };
    const heartbeatTime = robot.last_heartbeat ? new Date(robot.last_heartbeat).getTime() : 0;
    if (!heartbeatTime) return { label: '未上线', dot: 'bg-gray-400' };
    const offline = (Date.now() - heartbeatTime > 5 * 60 * 1000);
    return offline
      ? { label: '离线', dot: 'bg-gray-500' }
      : { label: '在线', dot: 'bg-green-500 animate-pulse' };
  }

  function getDisplayStatus(robot: any): { label: string; color: string } {
    if (robot.status === 'pending') return { label: '未批准', color: 'bg-amber-100 text-amber-700' };
    if (robot.status === 'kicked') return { label: '已踢出', color: 'bg-red-100 text-red-700' };
    return { label: '正常', color: 'bg-green-100 text-green-700' };
  }

  const joinUrl = joinToken
    ? `${window.location.origin}/groups/${id}/join?token=${joinToken}`
    : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lobster-red"></div>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
        >
          <Icons.ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{group.group_name}</h1>
          <p className="text-gray-500 mt-1">
            {group.external_platform ? `${group.external_platform} · ` : ''}
            {robots.length}/9 机器人
          </p>
        </div>
      </div>

      {/* 群组信息 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">群组信息</h2>
        </div>
        <div className="p-6 space-y-4">
          {/* 平台类型 */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">平台类型</label>
            <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm">
              {group.external_platform || '未设置'}
            </div>
          </div>

          {/* 外部群组ID */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">外部群组ID</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm font-mono truncate">
                {group.external_group_id || '未设置'}
              </code>
              {group.external_group_id && (
                <button
                  onClick={() => copyToClipboard(group.external_group_id, "已复制")}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
                  title="复制"
                >
                  <Icons.Copy className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* 群组链接 */}
          {group.is_owner && <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">群组链接</label>
            <div className="flex items-center gap-2">
              <a
                href={joinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-lobster-red text-sm truncate hover:underline"
                title={joinUrl || "加载中..."}
              >
                {joinUrl || "加载中..."}
              </a>
              {joinUrl && (
                <button
                  onClick={() => copyToClipboard(joinUrl, "链接已复制")}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
                  title="复制链接"
                >
                  <Icons.Copy className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>}
        </div>
      </div>

      {/* 成员列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">成员列表</h2>
          <p className="text-sm text-gray-500 mt-1">
            共 {robots.length} 个机器人
          </p>
        </div>

        {robots.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <Icons.Bot className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">还没有机器人</h3>
            <p className="text-gray-600">等待机器人通过加群链接加入</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {robots.map((robot) => {
              const displayStatus = getDisplayStatus(robot);
              return (
                <div
                  key={robot.id}
                  className="border border-gray-200 rounded-xl p-5 flex flex-col bg-white hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 truncate" style={{ maxWidth: '180px' }} title={robot.robot_name}>
                      {robot.robot_name}
                    </h3>
                    {robot.is_owner && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs shrink-0">群主</span>}
                    <div className="flex items-center gap-2 ml-2">
                      {robot.status !== 'kicked' && (() => {
                        const online = getOnlineStatus(robot);
                        return (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <span className={`w-2 h-2 rounded-full ${online.dot}`}></span>
                            {online.label}
                          </span>
                        );
                      })()}
                      <span className={`px-2 py-0.5 rounded text-xs ${displayStatus.color}`}>
                        {displayStatus.label}
                      </span>
                    </div>
                  </div>

                  {/* AK */}
                  {group.is_owner && <div className="flex items-start gap-2 mb-2">
                    <span className="text-gray-500 text-sm flex-shrink-0 mt-0.5">AK</span>
                    <code className="flex-1 min-w-0 text-gray-900 font-mono bg-gray-50 px-2 py-1.5 rounded text-xs truncate" title={robot.robot_ak || undefined}>
                      {robot.robot_ak}
                    </code>
                    <button
                      onClick={() => copyToClipboard(robot.robot_ak || '', 'AK 已复制')}
                      className="p-1.5 text-gray-400 hover:text-lobster-red hover:bg-gray-50 rounded flex-shrink-0"
                      title="复制 AK"
                    >
                      <Icons.Copy className="w-4 h-4" />
                    </button>
                  </div>}

                  {/* 会话信息 */}
                  {group.is_owner && robot.status !== 'pending' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-500 text-sm flex-shrink-0">会话</span>
                      <span className="text-gray-900 text-sm">
                        {robot.total_used || 0} / {robot.session_limit === 0 ? '∞' : robot.session_limit}
                      </span>
                      <button
                        onClick={() => handleResetCount(robot.id, robot.robot_name)}
                        disabled={robot.status === 'kicked'}
                        className={`text-sm flex items-center gap-1 ${robot.status === 'kicked' ? 'text-gray-400 cursor-not-allowed' : 'text-lobster-red hover:underline'}`}
                        title="重置计数"
                      >
                        <Icons.RefreshCw className="w-3.5 h-3.5" />
                        重置
                      </button>
                    </div>
                  )}

                  {/* 最后活跃 */}
                  {robot.status !== 'pending' && robot.last_active && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-500 text-sm flex-shrink-0">活跃</span>
                      <span className="text-gray-900 text-xs" title={new Date(robot.last_active).toLocaleString('zh-CN')}>
                        {new Date(robot.last_active).toLocaleString('zh-CN')}
                      </span>
                    </div>
                  )}

                  {group.is_owner && <div className="mt-auto pt-3 border-t border-gray-100">
                    {robot.status === 'pending' ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(robot.id, robot.robot_name)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded-lg flex items-center justify-center gap-1"
                        >
                          <Icons.CheckCircle className="w-4 h-4" />
                          批准
                        </button>
                        <button
                          onClick={() => handleReject(robot.id, robot.robot_name)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-2 rounded-lg flex items-center justify-center gap-1"
                        >
                          <Icons.XCircle className="w-4 h-4" />
                          拒绝
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-6">
                        <button
                          onClick={() => handleEditRobot(robot)}
                          disabled={robot.status === 'kicked'}
                          className={`text-sm flex items-center gap-1 ${robot.status === 'kicked' ? 'text-gray-400 cursor-not-allowed' : 'text-lobster-red hover:underline'}`}
                        >
                          <Icons.Edit className="w-3.5 h-3.5" />
                          编辑
                        </button>
                        <button
                          onClick={() => handleKickRobot(robot.id, robot.robot_name)}
                          disabled={robot.status === 'kicked'}
                          className={`text-sm flex items-center gap-1 ${robot.status === 'kicked' ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}`}
                        >
                          <Icons.LogOut className="w-3.5 h-3.5" />
                          踢出
                        </button>
                      </div>
                    )}
                  </div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <EditRobotModal
        robot={editingRobot}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={loadData}
      />
    </div>
  );
}
